import { FC, useEffect, useRef, useState } from "react";
import Select, {
  ActionMeta,
  SingleValue,
  MultiValue,
  InputActionMeta,
  components,
  StylesConfig,
  MenuListProps,
} from "react-select";
import { Label, Spinner, useThemeMode } from "flowbite-react";
import { HiPlus } from "react-icons/hi";

interface SelectOption {
  label: string;
  value: string;
}

// Defined at module scope so react-select keeps the same MenuList component
// identity across renders. Otherwise scroll position resets on every fetch.
const PaginatedMenuList = (props: MenuListProps) => {
  const { isFetching, isLoading, page, isLoadingMore } =
    ((props.selectProps as any)?.paginationState ?? {}) as {
      isFetching?: boolean;
      isLoading?: boolean;
      page?: number;
      isLoadingMore?: boolean;
    };
  const showInitialSpinner = (isFetching || isLoading) && page === 1;
  return (
    <components.MenuList {...props}>
      {showInitialSpinner ? (
        <div className="flex items-center justify-center py-4">
          <Spinner size="sm" />
        </div>
      ) : (
        <>
          {props.children}
          {isLoadingMore && (
            <div className="flex items-center justify-center py-2">
              <Spinner size="xs" />
            </div>
          )}
        </>
      )}
    </components.MenuList>
  );
};

const SELECT_COMPONENTS = { MenuList: PaginatedMenuList };

interface PaginatedSelectProps {
  id?: string;
  value: any;
  onChange: (
    newValue: SingleValue<any> | MultiValue<any>,
    actionMeta: ActionMeta<any>,
  ) => void;
  options: SelectOption[];
  onInputChange: (newValue: string, actionMeta: InputActionMeta) => void;
  isLoading?: boolean;
  placeholder?: string;
  isClearable?: boolean;
  page: number;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  isFetching?: boolean;
  onAddClick?: () => void;
  label?: string;
  menuMaxHeight?: number;
  isMulti?: boolean;
  isDisabled?: boolean;
}

const PaginatedSelect: FC<PaginatedSelectProps> = ({
  id,
  value,
  onChange,
  options,
  onInputChange,
  isLoading,
  placeholder,
  isClearable = true,
  page,
  totalPages,
  onPageChange,
  isFetching,
  onAddClick,
  label,
  menuMaxHeight = 200,
  isMulti = false,
  isDisabled = false,
}) => {
  const { mode } = useThemeMode();
  const isDark = mode === "dark";

  // Accumulate options across pages
  const [accumulatedOptions, setAccumulatedOptions] = useState<SelectOption[]>(
    [],
  );
  // Track the last page we actually appended so we don't double-append the
  // same page when RTK Query re-renders with the same args.
  const lastAppendedPageRef = useRef<number>(0);
  // Track the options content per page so we can ignore RTK Query's transient
  // empty/sticky data between arg changes.
  const lastOptionsKeyRef = useRef<string>("");

  useEffect(() => {
    if (isFetching || isLoading) return;

    const optionsKey = JSON.stringify(options);

    if (page === 1) {
      // New search or initial load — always sync with the current page-1 data.
      if (
        optionsKey !== lastOptionsKeyRef.current ||
        lastAppendedPageRef.current !== 1
      ) {
        setAccumulatedOptions(options);
        lastAppendedPageRef.current = 1;
        lastOptionsKeyRef.current = optionsKey;
      }
      return;
    }

    // Page > 1 path: only append once we have real data for the new page.
    if (page <= lastAppendedPageRef.current) return;
    if (options.length === 0) return;
    if (optionsKey === lastOptionsKeyRef.current) return; // stale/sticky data

    setAccumulatedOptions((prev) => {
      const existingValues = new Set(prev.map((o) => o.value));
      const newOptions = options.filter((o) => !existingValues.has(o.value));
      if (newOptions.length === 0) return prev;
      return [...prev, ...newOptions];
    });
    lastAppendedPageRef.current = page;
    lastOptionsKeyRef.current = optionsKey;
  }, [options, page, isFetching, isLoading]);

  const hasMore = page < (totalPages || 1);
  const isLoadingMore = (isFetching || isLoading) && page > 1;

  const handleMenuScrollToBottom = () => {
    if (hasMore && !isFetching && !isLoading) {
      onPageChange(page + 1);
    }
  };

  // react-select fires onInputChange for "input-change", "input-blur",
  // "menu-close", and "set-value". The last three reset the input to "" when
  // the menu closes or focus is lost — if we forwarded those, the parent's
  // search state would clear every time the dropdown loses focus.
  // "input-change" is the only event the user actually triggered (typing or
  // backspacing, including backspacing to empty), so we forward only that one.
  // Visible input value (shown only while the menu is open). It's separate
  // from `lastTypedValue` so we can blank the input when the menu closes —
  // otherwise it would overlay the selected option's label.
  const [inputValue, setInputValue] = useState("");
  // Sticky copy of whatever the user last typed. Survives menu close so the
  // search can be restored when the dropdown is reopened. Cleared only when
  // the user backspaces it away themselves.
  const [lastTypedValue, setLastTypedValue] = useState("");

  const handleMenuOpen = () => {
    setInputValue(lastTypedValue);
  };

  const handleMenuClose = () => {
    setInputValue("");
  };

  const handleInputChange = (newValue: string, meta: InputActionMeta) => {
    if (meta.action !== "input-change") return;
    setInputValue(newValue);
    setLastTypedValue(newValue);
    onInputChange(newValue, meta);
  };

  // The clear (×) indicator should reset everything — selection, visible
  // input, and the parent's search/filter state — so the dropdown returns to
  // its pristine state. react-select's default only clears the selected value.
  const handleChange = (
    newValue: SingleValue<any> | MultiValue<any>,
    actionMeta: ActionMeta<any>,
  ) => {
    if (actionMeta.action === "clear") {
      setInputValue("");
      setLastTypedValue("");
      onInputChange("", { action: "input-change", prevInputValue: lastTypedValue });
    }
    onChange(newValue, actionMeta);
  };

  const selectStyles: StylesConfig = {
    control: (base, state) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      borderColor: state.isFocused
        ? "#3b82f6"
        : isDark
          ? "#374151"
          : "#d1d5db",
      borderRadius: "0.5rem",
      minHeight: "38px",
      boxShadow: state.isFocused ? `0 0 0 1px #3b82f6` : "none",
      transition: "border-color 150ms, box-shadow 150ms",
      "&:hover": {
        borderColor: state.isFocused
          ? "#3b82f6"
          : isDark
            ? "#4b5563"
            : "#9ca3af",
      },
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: isDark ? "#1f2937" : "#ffffff",
      borderRadius: "0.5rem",
      border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
      boxShadow: isDark
        ? "0 10px 25px -5px rgba(0, 0, 0, 0.4)"
        : "0 10px 25px -5px rgba(0, 0, 0, 0.1)",
      overflow: "hidden",
      zIndex: 9999,
    }),
    menuList: (base) => ({
      ...base,
      padding: "4px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? isDark
          ? "#1d4ed8"
          : "#dbeafe"
        : state.isFocused
          ? isDark
            ? "#374151"
            : "#f3f4f6"
          : "transparent",
      color: state.isSelected
        ? isDark
          ? "#ffffff"
          : "#1e40af"
        : isDark
          ? "#d1d5db"
          : "#374151",
      borderRadius: "0.375rem",
      padding: "8px 10px",
      fontSize: "0.875rem",
      cursor: "pointer",
      transition: "background-color 100ms",
      "&:active": {
        backgroundColor: isDark ? "#1e3a5f" : "#bfdbfe",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: isDark ? "#f3f4f6" : "#111827",
      fontSize: "0.875rem",
    }),
    input: (base) => ({
      ...base,
      color: isDark ? "#f3f4f6" : "#111827",
      fontSize: "0.875rem",
    }),
    placeholder: (base) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
      fontSize: "0.875rem",
    }),
    clearIndicator: (base) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
      padding: "4px",
      "&:hover": {
        color: isDark ? "#d1d5db" : "#374151",
      },
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: isDark ? "#6b7280" : "#9ca3af",
      padding: "4px 8px",
      "&:hover": {
        color: isDark ? "#d1d5db" : "#374151",
      },
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  };

  return (
    <div className="w-full">
      <div className="mb-1.5 flex items-center justify-between">
        {label && <Label htmlFor={id} value={label} />}
        {onAddClick && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onAddClick();
            }}
            className="flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
            title={`Create new ${label}`}
          >
            <HiPlus className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      <Select
        inputId={id}
        // Stable class names on the (portalled) menu so popovers that close on
        // outside-click can tell a menu click from a real outside click.
        classNamePrefix="paginated-select"
        isMulti={isMulti}
        isDisabled={isDisabled}
        value={value}
        inputValue={inputValue}
        onChange={handleChange}
        options={accumulatedOptions}
        onInputChange={handleInputChange}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        isLoading={false}
        placeholder={placeholder}
        isClearable={isClearable}
        filterOption={() => true}
        styles={selectStyles}
        components={SELECT_COMPONENTS}
        onMenuScrollToBottom={handleMenuScrollToBottom}
        menuPortalTarget={
          typeof document !== "undefined" ? document.body : null
        }
        menuPosition="fixed"
        maxMenuHeight={menuMaxHeight}
        {...({
          paginationState: { isFetching, isLoading, page, isLoadingMore },
        } as any)}
      />
    </div>
  );
};

export default PaginatedSelect;
